
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, X, Download } from 'lucide-react';
import { useTeacherData } from '@/hooks/useTeacherData';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import TeachersPageSelectionDialog from './TeachersPageSelectionDialog';
import TeacherFormatDialog from './TeacherFormatDialog';

interface TeachersHeaderProps {
  onSearch: (query: string) => void;
}

export const TeachersHeader = ({ onSearch }: TeachersHeaderProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPageSelection, setShowPageSelection] = useState(false);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const { teachers } = useTeacherData();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch(query);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearch('');
  };

  const handleDownloadClick = () => {
    if (!teachers || teachers.length === 0) {
      toast.error('No teachers to download');
      return;
    }

    const teachersPerPage = 20;
    const totalPages = Math.ceil(teachers.length / teachersPerPage);

    // If only one page, show format dialog directly
    if (totalPages === 1) {
      setSelectedPages([]);
      setShowFormatDialog(true);
    } else {
      // Show page selection dialog first
      setShowPageSelection(true);
    }
  };

  const handlePageSelection = (startPage: number, endPage: number) => {
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    setSelectedPages(pages);
    setShowPageSelection(false);
    setShowFormatDialog(true);
  };

  const handleFormatDownload = async (format: 'jpg' | 'pdf') => {
    if (teachers && teachers.length > 0) {
      try {
        setIsDownloading(true);
        const { printTeachersListAsJPG, printTeachersListAsPDF } = await import('@/utils/printing/teacher');
        
        const teachersList = teachers.map(teacher => ({
          id: teacher.id || '',
          name: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
          department: teacher.department || 'N/A',
          position: teacher.position || 'N/A',
          email: teacher.email || 'N/A',
          phone: teacher.phone || 'N/A',
          qualifications: Array.isArray(teacher.qualifications) 
            ? teacher.qualifications.map(q => typeof q === 'string' ? q : (q?.degree || ''))
            : [],
          joinDate: teacher.joinDate ? new Date(teacher.joinDate).toLocaleDateString() : 'N/A',
          status: teacher.status || 'Active'
        }));
        
        if (format === 'jpg') {
          await printTeachersListAsJPG(teachersList, selectedPages.length > 0 ? selectedPages : undefined);
        } else {
          await printTeachersListAsPDF(teachersList, selectedPages.length > 0 ? selectedPages : undefined);
        }
      } catch (error) {
        console.error('Error downloading teachers list:', error);
        toast.error('Failed to download teachers list');
      } finally {
        setIsDownloading(false);
      }
    } else {
      toast.error('No teachers to download');
    }
  };

  const teachersPerPage = 20;
  const totalPages = Math.ceil((teachers?.length || 0) / teachersPerPage);

  return (
    <>
      <Helmet>
        <title>Teachers - NALC Student Program & Fee Tracker</title>
      </Helmet>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold">Teachers</h1>
        
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <Input
              type="text"
              placeholder="Search teachers..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pr-8"
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              aria-label="Search teachers"
            />
            {searchQuery ? (
              <button 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <Search className={`h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isSearchFocused ? 'text-primary' : 'text-gray-400'}`} />
            )}
          </div>
          
          <Button 
            variant="outline"
            onClick={handleDownloadClick}
            disabled={isDownloading}
            className="hidden md:flex items-center gap-2"
            aria-label="Download teachers list"
          >
            <Download className="h-4 w-4" />
            {isDownloading ? 'Downloading...' : 'Download List'}
          </Button>
          
          <Button asChild>
            <Link to="/teachers/new" aria-label="Add new teacher">
              <Plus className="h-4 w-4 mr-2" />
              Add Teacher
            </Link>
          </Button>
        </div>
      </div>

      <TeachersPageSelectionDialog
        isOpen={showPageSelection}
        onClose={() => setShowPageSelection(false)}
        totalTeachers={teachers?.length || 0}
        onDownload={handlePageSelection}
      />

      <TeacherFormatDialog
        isOpen={showFormatDialog}
        onClose={() => setShowFormatDialog(false)}
        onDownload={handleFormatDownload}
        downloadType="list"
      />
    </>
  );
};
